import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import type * as React from "react";

const buttonVariants = cva(
  [
    "inline-flex cursor-pointer items-center justify-center gap-1",
    "whitespace-nowrap shrink-0 outline-none transition-colors focus-visible:ring-[3px]",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary: [
          "bg-primary text-primary-foreground",
          "hover:bg-jade-600 active:bg-jade-600 dark:hover:bg-jade-700 dark:active:bg-jade-700",
          "focus-visible:ring-ring/50",
        ],
        secondary: [
          "bg-secondary text-foreground",
          "hover:bg-(--cta-secondary-hover) active:bg-(--cta-secondary-hover)",
          "focus-visible:inset-ring focus-visible:inset-ring-ring focus-visible:ring-ring/50",
        ],
        outline: [
          "border border-border bg-transparent text-foreground",
          "hover:bg-accent active:bg-accent",
          "focus-visible:border-ring focus-visible:ring-ring/50",
        ],
        ghost: [
          "bg-transparent text-foreground",
          "hover:bg-accent active:bg-accent",
          "focus-visible:ring-ring/50",
        ],
        destructive: [
          "bg-destructive text-primary-foreground",
          "hover:bg-destructive-400 active:bg-destructive-400 dark:hover:bg-destructive-500 dark:active:bg-destructive-500",
          "focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        ],
      },
      size: {
        mini: "h-6 label-sm px-1",
        sm: "h-8 label-sm px-2",
        default: "h-9 label-md px-3",
        lg: "h-10 label-md px-4",
        xl: "h-11 label-md px-6",
      },
      roundness: {
        default: "rounded-sm",
        round: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
      roundness: "default",
    },
  },
);

interface ButtonProps
  extends React.ComponentProps<"button">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({
  className,
  variant,
  size,
  roundness,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, roundness, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants, type ButtonProps };
